// src/features/workspace/common/utils/enumValueErrorClassifier.ts
//
// Turns a raw error string from a `metainfo:fetch-enum-values` / declare /
// dismiss call into a user-friendly `{ title, description, details }` triple.
// Consumers (DeclareEnumColumnDialog, EnumValuesDetailsDialog) render the
// triple via `OperationErrorAlert` — a friendly headline up top, optional
// one-paragraph explanation below it, and the raw error hidden behind a
// "Show details" toggle.
//
// The classifier knows about specific MSSQL error patterns where we have
// actionable guidance — most prominently the `OPENJSON` compat-level case
// (SQL Server raises `Msg 208 — Invalid object name 'OPENJSON'` whenever
// `COMPATIBILITY_LEVEL < 130`). The main-process `appendMssqlErrorHint` may
// or may not have appended its hint to the message yet; we match both the
// raw 208 form and the hint-augmented form so the classification is robust
// regardless of where the message was assembled.
//
// Unrecognized errors get a generic-but-still-friendly headline per
// operation, and the raw message still goes into details.

/**
 * The user-facing operation the dialog was trying to perform. Drives the
 * generic-fallback title when we don't have a specific match.
 */
export type EnumDialogOperation = 'sample' | 'declare' | 'dismiss';

/**
 * Context about the column / path the operation targeted. Lets us word
 * the title for a JSON property vs. an array element vs. a plain column.
 */
export type EnumDialogContext = {
  /** True when an `EnumColumnDeclaration` is JSON-path-scoped (carries a `path`). */
  isPath: boolean;
  /** Present iff `isPath` — array unnest vs. scalar property. */
  pathKind?: 'scalar' | 'array';
};

export type ClassifiedEnumError = {
  /** Short, user-friendly headline. */
  title: string;
  /** Optional one-paragraph explanation with action guidance. */
  description: string | null;
  /** Raw error string from the underlying call — surfaced in collapsed details. */
  details: string;
};

/**
 * `Invalid object name 'OPENJSON'` family of error 208. Tolerates the
 * various quoting styles SQL Server / the `mssql` Node driver use:
 * `'OPENJSON'`, `"OPENJSON"`, `` `OPENJSON` ``, `[OPENJSON]`, or unquoted.
 * Matches the same shape `electron/query-engine-adapters/mssqlErrorHints.ts`
 * uses to decide when to append the compat-level hint.
 */
const MSSQL_OPENJSON_PATTERN = /Invalid object name ['"`[]?OPENJSON/i;

/**
 * The signature of `appendMssqlErrorHint`'s addendum text. When the main
 * process has already appended it, this pattern matches even if the
 * upstream raw message has been transformed since.
 */
const MSSQL_OPENJSON_HINT_PATTERN = /requires SQL Server database compatibility level 130/i;

function isMssqlOpenjsonCompatError(message: string): boolean {
  return (
    MSSQL_OPENJSON_PATTERN.test(message) ||
    MSSQL_OPENJSON_HINT_PATTERN.test(message)
  );
}

function pathLabel(ctx: EnumDialogContext): string {
  if (!ctx.isPath) return 'column';
  return ctx.pathKind === 'array' ? 'JSON array' : 'JSON property';
}

/**
 * Classify a raw error message into a renderable triple.
 *
 * @param rawMessage The error message thrown from the underlying call.
 *                   Empty / whitespace-only strings still get a friendly
 *                   title; the empty body just won't expand to anything
 *                   meaningful (caller may suppress the toggle in that case).
 * @param operation  What the user was trying to do. Drives the fallback title.
 * @param ctx        Whether the target is a column, a JSON property, or a
 *                   JSON array — drives the wording.
 */
export function classifyEnumValueError(
  rawMessage: string,
  operation: EnumDialogOperation,
  ctx: EnumDialogContext,
): ClassifiedEnumError {
  const details = rawMessage ?? '';

  // Sample-time OPENJSON compat-level error — we have specific guidance.
  // (Declare / dismiss don't run SQL, so this branch is sample-only.)
  if (operation === 'sample' && isMssqlOpenjsonCompatError(details)) {
    return {
      title: `Can't sample distinct values from this ${pathLabel(ctx)}`,
      description:
        'This SQL Server database is on compatibility level below 130, ' +
        'so wildcard ([*]) paths and array sampling can\'t use OPENJSON. ' +
        'Either pick a scalar property without [*], or ask a DBA to run ' +
        '`ALTER DATABASE [<your-db>] SET COMPATIBILITY_LEVEL = 130;`.',
      details,
    };
  }

  // Generic, friendly per-operation title. The raw message still goes into
  // the collapsible details so a power user / support ticket has the full
  // signal.
  const title =
    operation === 'sample'
      ? `Couldn't sample values for this ${pathLabel(ctx)}`
      : operation === 'declare'
        ? 'Couldn\'t save the enum declaration'
        : 'Couldn\'t mark the column as not an enum';

  return { title, description: null, details };
}
